import { Alert, Delete } from 'baseui/icon';
import { Input, InputOverrides, InputProps } from 'baseui/input';
import { EyeCloseSVG, EyeOpenSVG } from 'common/assets';
import { themedUseStyletron } from 'common/styles/styletron.theme';
import * as React from 'react';
import merge from 'ts-deepmerge';

import CustomFormControl, {
  CustomFormControlProps,
} from '../custom-form-control';
import { styles } from '../styles';

interface Props
  extends InputProps,
    Omit<
      CustomFormControlProps,
      'name' | 'overrides' | 'positive' | 'error' | 'children'
    > {
  shouldShowError?: boolean;
  errorMessage?: string;
}

function Negative() {
  const [css, theme] = themedUseStyletron();

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.sizing.scale500,
        color: theme.colors.negative400,
      })}
    >
      <Alert size="18px" />
    </div>
  );
}

export default function MasterInput(props: Props) {
  const [, theme] = themedUseStyletron();
  const {
    shouldShowError,
    disabled,
    hideLabel,
    hideRequiredStar,
    errorMessage,
    label,
    noMargin,
  } = props;

  const overrides = React.useMemo(
    () =>
      merge(
        {
          ...(shouldShowError ? { After: Negative } : {}),
          Root: {
            style: {
              ...(disabled ? styles.reset : styles.active),
            },
          },
          Input: {
            style: {
              paddingTop: '2px',
              paddingBottom: '2px',
              ...(disabled ? styles.reset : styles.active),
            },
          },
          InputContainer: {
            style: {
              ...(disabled ? styles.reset : styles.active),
            },
          },
          ClearIcon: {
            component: ({ onClick }) => (
              <Delete
                //@ts-ignore
                onClick={onClick}
                overrides={{
                  Svg: {
                    style: {
                      cursor: 'pointer',
                    },
                  },
                }}
              />
            ),
          },
          MaskToggleShowIcon: {
            component: () => (
              <EyeCloseSVG
                style={{ cursor: 'pointer' }}
                color={theme.colors.skyDarkest}
              />
            ),
          },
          MaskToggleHideIcon: {
            component: () => (
              <EyeOpenSVG
                style={{ cursor: 'pointer' }}
                color={theme.colors.skyDarkest}
              />
            ),
          },
          StartEnhancer: {
            style: {
              backgroundColor: '#FFFFFF',
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
          EndEnhancer: {
            style: {
              backgroundColor: '#FFFFFF',
              paddingRight: '12px',
            },
          },
        } as InputOverrides,
        { ...props.overrides },
      ),

    [disabled, props.overrides, shouldShowError, theme.colors.skyDarkest],
  );

  return (
    <CustomFormControl
      disabled={disabled}
      required={props.required}
      hideLabel={hideLabel}
      hideRequiredStar={hideRequiredStar}
      error={errorMessage}
      label={label}
      noMargin={noMargin}
    >
      <Input {...props} overrides={overrides} />
    </CustomFormControl>
  );
}
